<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Account Officer Tracker</h1>
      </div>

      <div class="view-controls">
        <div class="view-buttons">
          <Select v-model="filterCongress" :options="congressOptions" optionLabel="label" optionValue="value" placeholder="All Congress" showClear class="filter-select" />
          <Button :label="'Table'" size="small" :severity="viewMode === 'table' ? undefined : 'secondary'" icon="pi pi-list" @click="viewMode = 'table'" />
          <Button :label="'Metrics'" size="small" :severity="viewMode === 'metrics' ? undefined : 'secondary'" icon="pi pi-chart-bar" @click="viewMode = 'metrics'" />
        </div>
      </div>

      <!-- Table View -->
      <DataTable v-if="viewMode === 'table'" :value="filteredRecords" :paginator="true" :rows="10" :loading="loading" stripedRows showGridlines v-model:filters="filters" filterDisplay="menu" scrollable scrollHeight="600px">
        <template #header>
          <div class="table-filters">
            <div class="filters-grid">
              <IconField iconPosition="left" class="search-field">
                <InputIcon class="pi pi-search" />
                <InputText v-model="filters['global'].value" placeholder="Search all columns..." />
              </IconField>
              <Select v-model="filterRequestingParty" :options="aoRequestingPartyOptions" optionLabel="label" optionValue="value" placeholder="Requesting Party" showClear class="filter-item" />
              <Select v-model="filterDivision" :options="aoDivisionOptions" optionLabel="label" optionValue="value" placeholder="Division" showClear class="filter-item" />
              <Select v-model="filterLoggedBy" :options="loggedByOptions" optionLabel="label" optionValue="value" placeholder="Logged By" showClear class="filter-item" />
              <Select v-model="filterLO" :options="loOfficerOptions" optionLabel="label" optionValue="value" placeholder="Assigned LO" showClear class="filter-item" />
              <Select v-model="filterAO" :options="aoOfficerOptions" optionLabel="label" optionValue="value" placeholder="Assigned AO" showClear class="filter-item"/>
              <Select v-model="filterSubjectTitle" :options="aoSubjectTitleOptions" optionLabel="label" optionValue="value" placeholder="Subject Title" showClear class="filter-item" filter />
            </div>
            <div class="filter-summary">
              <span class="result-count">{{ filteredRecords.length }} record(s)</span>
              <Button v-if="hasActiveAoFilters" label="Clear Filters" icon="pi pi-filter-slash" size="small" severity="secondary" text @click="clearAoFilters" />
            </div>
          </div>
        </template>
        <Column field="congress_period" header="Congress" sortable style="min-width: 80px" />
        <Column field="date_received" header="Date Received" sortable style="min-width: 110px" />
        <Column field="agenda" header="Agenda" sortable style="min-width: 200px">
          <template #body="{ data }">
            <span style="white-space: pre-line;">{{ data.agenda }}</span>
          </template>
        </Column>
        <Column field="requesting_party" header="Requesting Party" sortable style="min-width: 130px" />
         <Column field="logged_by" header="Logged By" sortable style="min-width: 130px" /> 
         <Column field="assigned_lo_name" header="Assigne LO" sortable style="min-width: 130px" />
        <Column field="account_officer_name" header="Assigne AO" sortable style="min-width: 130px" />
        <Column field="division" header="Division" sortable style="min-width: 100px" />
        <Column field="date_assigned" header="Date Assigned" sortable style="min-width: 110px" />
        <Column header="Actions" style="min-width: 90px">
          <template #body="{ data }">
            <div class="actions">
              <Button v-if="isDC" icon="pi pi-user-plus" severity="warn" size="small" @click="openAssignModal(data)" v-tooltip="'Assign Account Officer'" />
              <Button icon="pi pi-pencil" severity="info" size="small" :loading="editLoading === data.id" @click="openEditModal(data)" />
            </div>
          </template>
        </Column>
      </DataTable>

      <!-- Metrics View -->
      <div v-if="viewMode === 'metrics'" class="metrics-view">
        <div class="metrics-grid">
          <div class="metric-card">
            <h4>By Congress Period</h4>
            <div class="metric-bars">
              <div v-for="item in aoMetricsCongress" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container"><div class="metric-bar" :style="{ width: item.percent + '%' }"></div></div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card">
            <h4>By Requesting Party</h4>
            <div class="metric-bars">
              <div v-for="item in aoMetricsRequestingParty" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container"><div class="metric-bar pie-bar" :style="{ width: item.percent + '%' }"></div></div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card" v-if="isDC">
            <h4>By Account Officer</h4>
            <div class="metric-bars">
              <div v-for="item in aoMetricsOfficer" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container"><div class="metric-bar stacked-bar" :style="{ width: item.percent + '%' }"></div></div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card" v-if="isDC">
            <h4>By Division</h4>
            <div class="metric-bars">
              <div v-for="item in aoMetricsDivision" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container"><div class="metric-bar logged-bar" :style="{ width: item.percent + '%' }"></div></div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PLLG DC: Assign Account Officer Dialog -->
    <Dialog v-model:visible="showAssignModal" header="Assign Account Officer" :modal="true" :style="{width: '450px'}">
      <div class="assign-form">
        <p>Agenda: <strong>{{ assignRecord?.agenda }}</strong></p>
        <div class="form-group" v-if="isPolicyDC">
          <FloatLabel variant="in">
            <Select id="assign_division" v-model="assignDivision" :options="divisionOptions" optionLabel="label" optionValue="value" class="w-full" />
            <label for="assign_division">Division</label>
          </FloatLabel>
        </div>
        <div class="form-group" v-if="assignDivision !== 'Liaison' || isLiaisonDC">
          <FloatLabel variant="in">
            <Select id="assign_ao" v-model="assignAoId" :options="aoUsers" optionLabel="name" optionValue="id" filter class="w-full" />
            <label for="assign_ao">Account Officer</label>
          </FloatLabel>
        </div>
        <div v-if="isPolicyDC && assignDivision === 'Liaison'" class="info-card">
          <i class="pi pi-info-circle"></i>
          <span>Liaison DC will assign the Account Officer.</span>
        </div>
      </div>
      <div class="modal-actions">
        <Button label="Cancel" severity="secondary" icon="pi pi-times" @click="showAssignModal = false" />
        <Button label="Assign" icon="pi pi-check" :loading="assigning" @click="saveAssign" />
      </div>
    </Dialog>

    <!-- Account Officer: Edit Record Dialog -->
    <Dialog v-model:visible="showEditModal" header="Record Details" :modal="true" :maximizable="true" :style="{width: '100vw', height: '100vh'}" :contentStyle="{height: '100%'}" position="top">
      <div class="detail-grid">
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">Congress</span>
            <span class="detail-value">{{ currentRecord.congress_period }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Date Received</span>
            <span class="detail-value">{{ currentRecord.date_received }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Agenda</span>
            <span class="detail-value">{{ currentRecord.agenda }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Requesting Party</span>
            <span class="detail-value">{{ currentRecord.requesting_party }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Date Assigned</span>
            <span class="detail-value">{{ currentRecord.date_assigned || '—' }}</span>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">House Bill No.</span>
            <span class="detail-value">{{ formatArray(currentRecord.house_bill_no) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Senate Bill No.</span>
            <span class="detail-value">{{ formatArray(currentRecord.senate_bill_no) }}</span>
          </div>
        </div>

        <div class="editable-section">
          <div class="form-row-3">
            <div class="form-group">
              <FloatLabel variant="in">
                <MultiSelect id="edit_tier" v-model="currentRecord.tier" :options="tierOptions" optionLabel="label" optionValue="value" :disabled="isReadOnly"/>
                <label for="edit_tier">Types of Proposal</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <div class="field-with-add">
                <FloatLabel variant="in" class="flex-grow">
                  <Select id="edit_subject_title" v-model="currentRecord.subject_title" :options="subjectTitleOptions" optionLabel="name" optionValue="name" filter />
                  <label for="edit_subject_title">Subject Title</label>
                </FloatLabel>
                <Button icon="pi pi-plus" severity="success" size="small" @click="showAddSubjectTitle = true" v-tooltip="'Add New Subject Title'" :disabled="isReadOnly"/>
              </div>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="edit_lrc" v-model="currentRecord.legislative_request_classification" :options="lrcOptions" optionLabel="label" optionValue="value"/>
                <label for="edit_lrc">Legislative Request Classification</label>
              </FloatLabel>
            </div>
          </div>

          <div class="form-row-3">
            <div class="form-group">
              <FloatLabel variant="in">
                <MultiSelect id="edit_tax_type" v-model="currentRecord.types_of_tax" :options="taxTypeOptions" optionLabel="label" optionValue="value" :disabled="isReadOnly" filter />
                <label for="edit_tax_type">Types of Tax</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <DatePicker id="edit_date_filed" v-model="currentRecord.date_filed" dateFormat="yy-mm-dd" :disabled="isReadOnly"/>
                <label for="edit_date_filed">Date Filed</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <MultiSelect id="edit_authors" v-model="currentRecord.authors" :options="authorOptions" optionLabel="name" optionValue="name" filter display="chip" :disabled="isReadOnly" />
                <label for="edit_authors">Authors</label>
              </FloatLabel>
            </div>
          </div>

          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_long_title" v-model="currentRecord.long_title" :disabled="isReadOnly"/>
              <label for="edit_long_title">Long Title</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_short_title" v-model="currentRecord.short_title" :disabled="isReadOnly"/>
              <label for="edit_short_title">Short Title</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_briefer_link" v-model="currentRecord.briefer_link" />
              <label for="edit_briefer_link">Briefer Link</label>
            </FloatLabel>
          </div>
          <!--<div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_review_remarks" v-model="currentRecord.review_remarks" rows="2" :disabled="isReadOnly"/>
              <label for="edit_review_remarks">Review Remarks</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_salient_features" v-model="currentRecord.salient_features" rows="2" />
              <label for="edit_salient_features">Salient Features</label>
            </FloatLabel>
          </div>-->
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_background" v-model="currentRecord.background_rationale" rows="2" :disabled="isReadOnly"/>
              <label for="edit_background">Background/Rationale</label>
            </FloatLabel>
          </div>
          <!-- <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_current_regime" v-model="currentRecord.current_existing_regime" rows="2" :disabled="isReadOnly"/>
              <label for="edit_current_regime">Current/Existing Regime</label>
            </FloatLabel>
          </div> 
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_proposal" v-model="currentRecord.proposal_salient_provisions" rows="2" :disabled="isReadOnly"/>
              <label for="edit_proposal">Proposal/Salient Provisions</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_fpmg" v-model="currentRecord.fpmg_recommendation_positions" rows="2" :disabled="isReadOnly"/>
              <label for="edit_fpmg">FPMG Recommendation Positions</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_osec" v-model="currentRecord.osec_instruction_remarks" rows="2" :disabled="isReadOnly"/>
              <label for="edit_osec">OSEC Instruction/Remarks</label>
            </FloatLabel>
          </div>-->
        </div>

        <!-- Position Papers -->
        <div class="section">
          <div class="section-header">
            <h3>Position Papers</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addPositionPaper" :disabled="isReadOnly"/>
          </div>
          <div v-for="(pp, ppIndex) in positionPapers" :key="pp.id || ppIndex" class="pp-card">
            <div class="pp-header">
              <h4>Position Paper #{{ ppIndex + 1 }}</h4>
              <Button icon="pi pi-trash" severity="danger" size="small" text @click="positionPapers.splice(ppIndex, 1)" :disabled="isReadOnly"/>
            </div>
            <div class="pp-fields">
                <div class="form-group">
                  <FloatLabel variant="in">
                    <InputText v-model="pp.title" :disabled="isReadOnly"/>
                    <label>Title</label>
                  </FloatLabel>
                </div>
                <div class="form-group">
                  <FloatLabel variant="in">
                    <InputText v-model="pp.draft_position_paper_link" :disabled="isReadOnly"/>
                    <label>Draft Position Paper Link</label>
                  </FloatLabel>
                </div>
                <div class="form-group">
                  <FloatLabel variant="in">
                    <InputText v-model="pp.draft_docket_link" :disabled="isReadOnly"/>
                    <label>Draft Docket Link</label>
                  </FloatLabel>
                </div>
                <div class="form-group">
                  <FloatLabel variant="in">
                    <InputText v-model="pp.signed_position_paper_link" :disabled="isReadOnly"/>
                    <label>Signed Position Paper Link</label>
                  </FloatLabel>
                </div>
            </div>

            <!-- Status entries sub-table -->
            <div class="pp-statuses">
              <div class="section-header">
                <h5>Status History</h5>
                <Button icon="pi pi-plus" label="Add Status" size="small" severity="secondary" @click="pp.statuses.push({ status: '', date: null })" :disabled="isReadOnly"/>
              </div>
              <DataTable :value="pp.statuses" showGridlines size="small" v-if="pp.statuses && pp.statuses.length">
                <Column field="status" header="Status">
                  <template #body="{ data: s }">
                    <Select v-model="s.status" :options="ppStatusOptions" optionLabel="label" optionValue="value" class="w-full" :disabled="isReadOnly"/>
                  </template>
                </Column>
                <Column field="date" header="Date">
                  <template #body="{ data: s }">
                    <DatePicker v-model="s.date" dateFormat="yy-mm-dd" class="w-full" :disabled="isReadOnly"/>
                  </template>
                </Column>
                <Column header="" style="width: 4rem">
                  <template #body="{ data: s, index: sIndex }">
                    <Button icon="pi pi-trash" severity="danger" size="small" text @click="pp.statuses.splice(sIndex, 1)" :disabled="isReadOnly"/>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </div>

        <!-- Key Issues Section -->
        <div class="key-issues-wrapper">
          <KeyIssuesSection
            :keyIssues="keyIssues"
            v-model:activeTab="keyIssuesActiveTab"
            :revenueStartYear="revenueStartYear"
            :revenueEndYear="revenueEndYear"
            :budgetaryStartYear="budgetaryStartYear"
            :budgetaryEndYear="budgetaryEndYear"
            :readOnly="isReadOnly"
            @addRevenueRow="addRevenueRow"
            @addBudgetaryRow="addBudgetaryRow"
            @addDofConcernsRow="addDofConcernsRow"
            @deleteRevenueRow="deleteRevenueRow"
            @deleteBudgetaryRow="deleteBudgetaryRow"
            @deleteDofConcernsRow="deleteDofConcernsRow"
            @updateRevenueStartYear="(y) => revenueStartYear = y"
            @updateRevenueEndYear="(y) => revenueEndYear = y"
            @updateBudgetaryStartYear="(y) => budgetaryStartYear = y"
            @updateBudgetaryEndYear="(y) => budgetaryEndYear = y"
          />
        </div>

        <!-- Referral Inputs Section -->
        <div class="section">
          <div class="section-header">
            <h3>Referral Inputs</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addReferralInput(currentRecord.id)" :disabled="isReadOnly"/>
          </div>
          <DataTable :value="referralInputs" showGridlines size="small">
            <Column field="referred_to" header="Referred To">
              <template #body="{ data }">
                <Select v-model="data.referred_to" :options="agencyOptions" optionLabel="name" optionValue="name" class="w-full" :disabled="isReadOnly" filter />
              </template>
            </Column>
            <Column field="received_date" header="Date Received">
              <template #body="{ data }">
                <DatePicker v-model="data.received_date" dateFormat="yy-mm-dd" class="w-full" :disabled="isReadOnly"/>
              </template>
            </Column>
            <Column field="pending_inputs" header="Pending Inputs">
              <template #body="{ data }">
                <Textarea v-model="data.pending_inputs" rows="2" class="w-full" autoResize :disabled="isReadOnly"/>
              </template>
            </Column>
            <Column field="drive_link" header="Drive Link">
              <template #body="{ data }">
                <Textarea v-model="data.drive_link" rows="2" class="w-full" :disabled="isReadOnly" autoResize />
              </template>
            </Column>
            <Column header="" style="width: 4rem">
              <template #body="{ data, index }">
                <Button icon="pi pi-trash" severity="danger" size="small" text @click="referralInputs.splice(index, 1)" :disabled="isReadOnly"/>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
      <div class="modal-actions-right">
        <Button label="Save Changes" icon="pi pi-save" @click="saveAllChanges" :loading="saving" />
      </div>
    </Dialog>

    <!-- Add Subject Title Dialog -->
    <Dialog v-model:visible="showAddSubjectTitle" header="Add New Subject Title" :modal="true" :style="{width: '400px'}">
      <div class="assign-form">
        <div class="form-group">
          <FloatLabel variant="in">
            <InputText id="new_subject_title" v-model="newSubjectTitleName" class="w-full" />
            <label for="new_subject_title">Subject Title Name *</label>
          </FloatLabel>
        </div>
      </div>
      <div class="modal-actions">
        <Button label="Cancel" severity="secondary" icon="pi pi-times" @click="showAddSubjectTitle = false; newSubjectTitleName = ''" />
        <Button label="Save" icon="pi pi-check" :loading="savingSubjectTitle" @click="saveNewSubjectTitle" />
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import Swal from 'sweetalert2'
import api from '../axios.js'
import KeyIssuesSection from './KeyIssuesSection.vue'

const { proxy } = getCurrentInstance()

const records = ref([])
const loading = ref(false)
const editLoading = ref(null)
const saving = ref(false)
const currentRecord = ref({})
const showEditModal = ref(false)

const showAssignModal = ref(false)
const assignRecord = ref(null)
const assignAoId = ref(null)
const assignDivision = ref(null)
const assigning = ref(false)
const aoUsers = ref([])
const subjectTitleOptions = ref([])
const authorOptions = ref([])
const positionPapers = ref([])
const ppSaving = ref(null)
const referralInputs = ref([])
const agencyOptions = ref([])
const showAddSubjectTitle = ref(false)
const newSubjectTitleName = ref('')
const savingSubjectTitle = ref(false)
const riSaving = ref(null)
const keyIssuesActiveTab = ref('revenue')
const revenueStartYear = ref(new Date().getFullYear())
const revenueEndYear = ref(new Date().getFullYear() + 5)
const budgetaryStartYear = ref(new Date().getFullYear())
const budgetaryEndYear = ref(new Date().getFullYear() + 5)
const keyIssues = ref({
  revenue_rows: [{ particulars: '' }],
  budgetary_rows: [{ particulars: '' }],
  dof_concerns_rows: [{ concerns: '' }]
})
const keyIssuesSaving = ref(false)

const filters = ref({
  global: { value: null, matchMode: 'contains' }
})

const congressOptions = [
  { label: '20th Congress', value: '20' },
  { label: '19th Congress', value: '19' }
]

const viewMode = ref('table')
const filterCongress = ref('20')
const filterRequestingParty = ref(null)
const filterDivision = ref(null)
const filterLoggedBy = ref(null)
const filterLO = ref(null);
const filterAO = ref(null)
const filterSubjectTitle = ref(null)

const aoRequestingPartyOptions = computed(() => {
  const parties = [...new Set(records.value.map(r => r.requesting_party).filter(Boolean))]
  return parties.sort().map(p => ({ label: p, value: p }))
})

const aoDivisionOptions = computed(() => {
  const divs = [...new Set(records.value.map(r => r.division).filter(Boolean))]
  return divs.sort().map(d => ({ label: d, value: d }))
})

const loggedByOptions = computed(() => {
  const officers = [...new Set(records.value.map(r => r.logged_by).filter(Boolean))]
  return officers.sort().map(o => ({ label: o, value: o }))
})

const loOfficerOptions = computed(() => {
  const officers = [...new Set(records.value.map(r => r.assigned_lo_name).filter(Boolean))]
  return officers.sort().map(o => ({ label: o, value: o }))
})

const aoOfficerOptions = computed(() => {
  const officers = [...new Set(records.value.map(r => r.account_officer_name).filter(Boolean))]
  return officers.sort().map(o => ({ label: o, value: o }))
})

const aoSubjectTitleOptions = computed(() => {
  const titles = [...new Set(records.value.map(r => r.subject_title).filter(Boolean))]
  return titles.sort().map(t => ({ label: t, value: t }))
})

const filteredRecords = computed(() => {
  return records.value.filter(r => {
    if (filterCongress.value && String(r.congress_period) !== String(filterCongress.value)) return false
    if (filterRequestingParty.value && r.requesting_party !== filterRequestingParty.value) return false
    if (filterDivision.value && r.division !== filterDivision.value) return false
    if (filterLoggedBy.value && r.logged_by !== filterLoggedBy.value) return false
    if (filterLO.value && r.assigned_lo_name !== filterLO.value) return false
    if (filterAO.value && r.account_officer_name !== filterAO.value) return false

    if (filterSubjectTitle.value && r.subject_title !== filterSubjectTitle.value) return false
    return true
  })
})

const hasActiveAoFilters = computed(() => {
  return filterRequestingParty.value || filterDivision.value || filterAO.value || filterSubjectTitle.value
})

const isReadOnly = computed(() => {
  const lrc = currentRecord.value.legislative_request_classification
  return lrc === 'Update' || lrc === 'Follow-up'
})

const clearAoFilters = () => {
  filterRequestingParty.value = null
  filterDivision.value = null
  filterLoggedBy.value = null
  filterLO.value = null
  filterAO.value = null
  filterSubjectTitle.value = null
}

// Metrics
const buildAoMetric = (data, field, limit) => {
  const counts = {}
  data.forEach(r => { const val = r[field] || 'Unassigned'; counts[val] = (counts[val] || 0) + 1 })
  let sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
  if (limit) sorted = sorted.slice(0, limit)
  const max = sorted.length > 0 ? sorted[0][1] : 1
  return sorted.map(([label, count]) => ({ label, count, percent: (count / max) * 100 }))
}

const aoMetricsCongress = computed(() => buildAoMetric(filteredRecords.value, 'congress_period'))
const aoMetricsRequestingParty = computed(() => buildAoMetric(filteredRecords.value, 'requesting_party'))
const aoMetricsOfficer = computed(() => buildAoMetric(filteredRecords.value, 'account_officer_name', 10))
const aoMetricsDivision = computed(() => buildAoMetric(filteredRecords.value, 'division'))

const isDC = computed(() => {
  const roles = proxy.$authStore.user?.role || []
  return roles.includes('pllg-dc') || roles.includes('pllg-dc-policy') || roles.includes('pllg-dc-liaison')
})

const isPolicyDC = computed(() => {
  const roles = proxy.$authStore.user?.role || []
  return roles.includes('pllg-dc-policy') || roles.includes('pllg-dc')
})
const isLiaisonDC = computed(() => proxy.$authStore.user?.role?.includes('pllg-dc-liaison'))

const tierOptions = [
  { label: 'Tax', value: 'Tax' },
  { label: 'Budgetary', value: 'Budgetary' },
  { label: 'Other DOF Concerns', value: 'Other DOF Concerns' },

  // { label: 'Tier II OP OSEC WAM RED Bill', value: 'Tier II OP OSEC WAM RED Bill' },
  // { label: 'Tier I other tax, non tax/SSS', value: 'Tier I other tax, non tax/SSS' }
]

const divisionOptions = [
  { label: 'Liaison', value: 'Liaison' },
  { label: 'Policy', value: 'Policy' }
]

const lrcOptions = [
  { label: 'New', value: 'New' },
  { label: 'Follow-up', value: 'Follow-up' },
  { label: 'Update', value: 'Update' }
]

const taxTypeOptions = [
  { label: 'CIT deduction', value: 'CIT deduction' },
  { label: 'CIT exemption', value: 'CIT exemption' },
  { label: 'Custom duties', value: 'Custom duties' },
  { label: 'Domestic top up', value: 'Domestic top up' },
  { label: 'Donors tax', value: 'Donors tax' },
  { label: 'DST', value: 'DST' },
  { label: 'Estate tax', value: 'Estate tax' },
  { label: 'Excise Tax', value: 'Excise Tax' },
  { label: 'Fiscal incentives (RBEs)', value: 'Fiscal incentives (RBEs)' },
  { label: 'Local taxes', value: 'Local taxes' },
  { label: 'None', value: 'None' },
  { label: 'Other taxes', value: 'Other taxes' },
  { label: 'PIT deduction', value: 'PIT deduction' },
  { label: 'PIT exemption', value: 'PIT exemption' },
  { label: 'Real property tax', value: 'Real property tax' },
  { label: 'Tax Admin', value: 'Tax Admin' },
  { label: 'Tax Amnesty', value: 'Tax Amnesty' },
  { label: 'Tax Credit', value: 'Tax Credit' },
  { label: 'VAT', value: 'VAT' }
]

const ppStatusOptions = [
  // { label: 'Drafting', value: 'Drafting' },
  // { label: 'For updating', value: 'For updating' },
  { label: 'Submitted by AO', value: 'Submitted by AO' },
  { label: 'Cleared by PRLO Assistant DC - Policy A', value: 'Cleared by PRLO Assistant DC - Policy A'},
  { label: 'Cleared by PRLO Chief - Policy A - AVC', value: 'Cleared by PRLO Chief - Policy A - AVC' },
  { label: 'Cleared by Chief - Liaison B - JG', value: 'Cleared by Chief - Liaison B - JG'},
  { label: 'Cleared by Office of the FPMG Undersecretary - EJS', value: 'Cleared by Office of the FPMG Undersecretary - EJS'},
  { label: 'Cleared by DJB', value: 'Cleared by DJB' },
  { label: 'Cleared by MKLE', value: 'Cleared by MKLE'},
  { label: 'Cleared by Office of the FPMG Undersecretary - DLS', value: 'Cleared by Office of the FPMG Undersecretary - DLS' },
  { label: 'Cleared by UKA', value: 'Cleared by UKA'},
  { label: 'For reclearance', value: 'For reclearance' },
  { label: 'Transmitted to Legal', value: 'Transmitted to Legal' },
  { label: 'Transmitted to OSEC', value: 'Transmitted to OSEC' },
  { label: 'Signed by Secretary', value: 'Signed by Secretary' },
  { label: 'Assigned to other Office', value: 'Assigned to other Office' }
]

const formatArray = (arr) => {
  if (!arr || !arr.length) return '—'
  return Array.isArray(arr) ? arr.join(', ') : arr
}

const loadRecords = async () => {
  loading.value = true
  try {
    let url = '/ao-tracker'
    if (isPolicyDC.value) {
      url = '/ao-tracker/dc'
    } else if (isLiaisonDC.value) {
      url = '/ao-tracker/dc?division=Liaison'
    }
    const res = await api.get(url)
    records.value = res.data
  } catch (e) { console.error(e) }
  finally { loading.value = false }
}

const loadAOUsers = async () => {
  try {
    const res = await api.get('/lo-officers')
    aoUsers.value = res.data
  } catch (e) { console.error(e) }
}

const loadSubjectTitles = async () => {
  try {
    const res = await api.get('/subject-titles')
    subjectTitleOptions.value = res.data
  } catch (e) { console.error(e) }
}

const saveNewSubjectTitle = async () => {
  if (!newSubjectTitleName.value.trim()) return
  savingSubjectTitle.value = true
  try {
    await api.post('/subject-titles', { name: newSubjectTitleName.value.trim() })
    await loadSubjectTitles()
    currentRecord.value.subject_title = newSubjectTitleName.value.trim()
    showAddSubjectTitle.value = false
    newSubjectTitleName.value = ''
    Swal.fire({ title: 'Added!', text: 'Subject title added successfully', icon: 'success', timer: 1500, showConfirmButton: false })
  } catch (e) {
    const msg = e.response?.data?.message || 'Failed to add subject title'
    Swal.fire({ title: 'Error!', text: msg, icon: 'error' })
  } finally { savingSubjectTitle.value = false }
}

const loadAuthors = async (congressNo = '20') => {
  try {
    const [senRes, repRes] = await Promise.all([
      api.get(`/senators?congress_no=${congressNo}`),
      api.get(`/representatives?congress_no=${congressNo}`)
    ])
    authorOptions.value = [...senRes.data, ...repRes.data].sort((a, b) => a.name.localeCompare(b.name))
  } catch (e) { console.error(e) }
}

// PLLG DC: Assign
const openAssignModal = (record) => {
  assignRecord.value = record
  assignAoId.value = record.account_officer_id
  assignDivision.value = isLiaisonDC.value ? 'Liaison' : (record.division || null)
  showAssignModal.value = true
}

const saveAssign = async () => {
  if (!assignDivision.value) return
  // AO is required unless Policy DC is assigning to Liaison
  if (assignDivision.value !== 'Liaison' && !assignAoId.value) return
  if (isLiaisonDC.value && !assignAoId.value) return

  const result = await Swal.fire({
    title: 'Assign Account Officer?',
    text: assignDivision.value === 'Liaison' && !assignAoId.value
      ? 'This will assign to Liaison division. Liaison DC will assign the Account Officer.'
      : 'Are you sure you want to assign this Account Officer?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Assign',
    cancelButtonText: 'Cancel'
  })
  if (!result.isConfirmed) return

  assigning.value = true
  try {
    await api.put(`/ao-tracker/${assignRecord.value.id}/assign`, {
      account_officer_id: assignAoId.value || null,
      division: assignDivision.value
    })
    showAssignModal.value = false
    loadRecords()
    Swal.fire({ title: 'Assigned!', text: 'Assignment saved successfully', icon: 'success', timer: 1500, showConfirmButton: false })
  } catch (e) {
    Swal.fire({ title: 'Error!', text: 'Failed to assign', icon: 'error' })
  } finally { assigning.value = false }
}

// Account Officer: Edit
const openEditModal = async (record) => {
  editLoading.value = record.id
  try {
    const res = await api.get(`/ao-tracker/${record.id}`)
    currentRecord.value = res.data
    positionPapers.value = (res.data.position_papers || []).map(pp => ({
      ...pp,
      statuses: (pp.statuses || []).map(s => ({ ...s }))
    }))
    const loadedKeyIssues = res.data.key_issues || {
      revenue_rows: [],
      budgetary_rows: [],
      dof_concerns_rows: []
    }
    // Ensure particulars_rows is always initialized
    if (loadedKeyIssues.revenue_rows) {
      loadedKeyIssues.revenue_rows = loadedKeyIssues.revenue_rows.map(row => ({
        ...row,
        particulars_rows: row.particulars_rows || [{ particulars: row.particulars || '' }]
      }))
    }
    if (loadedKeyIssues.budgetary_rows) {
      loadedKeyIssues.budgetary_rows = loadedKeyIssues.budgetary_rows.map(row => ({
        ...row,
        particulars_rows: row.particulars_rows || [{ particulars: row.particulars || '' }]
      }))
    }
    keyIssues.value = loadedKeyIssues
    referralInputs.value = (res.data.referral_inputs || []).map(ri => ({ ...ri }))
    // Load authors filtered by congress period
    if (res.data.congress_period) {
      loadAuthors(res.data.congress_period)
    }
    showEditModal.value = true
  } catch (e) { console.error(e) }
  finally { editLoading.value = null }
}

const saveChanges = async () => {
  saving.value = true
  try {
    await api.put(`/ao-tracker/${currentRecord.value.id}`, currentRecord.value)
    loadRecords()
    Swal.fire({ title: 'Saved!', text: 'Changes saved successfully', icon: 'success' })
  } catch (e) {
    Swal.fire({ title: 'Error!', text: 'Failed to save changes', icon: 'error' })
  } finally { saving.value = false }
}

const saveAllChanges = async () => {
  // Validate required fields
  if (!currentRecord.value.subject_title) {
    Swal.fire({ title: 'Validation', text: 'Subject Title is required.', icon: 'warning' })
    return
  }
  if (!currentRecord.value.legislative_request_classification) {
    Swal.fire({ title: 'Validation', text: 'Legislative Request Classification is required.', icon: 'warning' })
    return
  }

  saving.value = true
  try {
    await api.put(`/ao-tracker/${currentRecord.value.id}`, {
      ...currentRecord.value,
      position_papers: positionPapers.value.map(pp => ({
        title: pp.title,
        position: pp.position,
        draft_position_paper_link: pp.draft_position_paper_link,
        draft_docket_link: pp.draft_docket_link,
        signed_position_paper_link: pp.signed_position_paper_link,
        statuses: (pp.statuses || []).map(s => ({ status: s.status, date: s.date }))
      })),
      key_issues: keyIssues.value,
      revenue_start_year: revenueStartYear.value,
      revenue_end_year: revenueEndYear.value,
      budgetary_start_year: budgetaryStartYear.value,
      budgetary_end_year: budgetaryEndYear.value,
      referral_inputs: referralInputs.value.map(ri => ({
        id: ri.id || null,
        referred_to: ri.referred_to,
        received_date: ri.received_date,
        pending_inputs: ri.pending_inputs,
        drive_link: ri.drive_link,
        referral_id: ri.referral_id,
      }))
    })
    loadRecords()
    Swal.fire({ title: 'Saved!', text: 'All changes saved successfully', icon: 'success' })
  } catch (e) {
    console.error(e)
    Swal.fire({ title: 'Error!', text: 'Failed to save changes', icon: 'error' })
  } finally { saving.value = false }
}

// ============================================================================
// POSITION PAPERS CRUD
// ============================================================================

const addPositionPaper = () => {
  positionPapers.value.push({ title: '', position: '', draft_position_paper_link: '', draft_docket_link: '', signed_position_paper_link: '', statuses: [] })
}

const editPositionPaper = (index) => {
  const pp = positionPapers.value[index]
  pp._backup = { title: pp.title, position: pp.position, draft_position_paper_link: pp.draft_position_paper_link, draft_docket_link: pp.draft_docket_link, signed_position_paper_link: pp.signed_position_paper_link }
  pp.editing = true
}

const cancelPositionPaper = (index) => {
  if (positionPapers.value[index].isNew) {
    positionPapers.value.splice(index, 1)
  } else {
    const backup = positionPapers.value[index]._backup
    Object.assign(positionPapers.value[index], backup, { editing: false })
  }
}

const savePositionPaper = async (pp, index) => {
  ppSaving.value = index
  try {
    if (pp.isNew) {
      const res = await api.post(`/ao-tracker/${currentRecord.value.id}/position-papers`, {
        title: pp.title, position: pp.position, draft_position_paper_link: pp.draft_position_paper_link, draft_docket_link: pp.draft_docket_link, signed_position_paper_link: pp.signed_position_paper_link
      })
      positionPapers.value[index] = { ...res.data, statuses: res.data.statuses || [], editing: false }
    } else {
      await api.put(`/ao-tracker/${currentRecord.value.id}/position-papers/${pp.id}`, {
        title: pp.title, position: pp.position, draft_position_paper_link: pp.draft_position_paper_link, draft_docket_link: pp.draft_docket_link, signed_position_paper_link: pp.signed_position_paper_link
      })
      pp.editing = false
    }
    delete pp.isNew
    delete pp._backup
  } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' }) }
  finally { ppSaving.value = null }
}

const deletePositionPaper = async (index) => {
  const pp = positionPapers.value[index]
  const result = await Swal.fire({ title: 'Delete?', text: 'Delete this position paper and all its statuses?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
  if (result.isConfirmed) {
    try {
      await api.delete(`/ao-tracker/${currentRecord.value.id}/position-papers/${pp.id}`)
      positionPapers.value.splice(index, 1)
    } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' }) }
  }
}

// ============================================================================
// POSITION PAPER STATUSES CRUD
// ============================================================================

const addPPStatus = (ppIndex) => {
  positionPapers.value[ppIndex].statuses.push({ status: '', date: null, editing: true, isNew: true })
}

const editPPStatus = (ppIndex, sIndex) => {
  const s = positionPapers.value[ppIndex].statuses[sIndex]
  s._backup = { status: s.status, date: s.date }
  s.editing = true
}

const cancelPPStatus = (ppIndex, sIndex) => {
  const s = positionPapers.value[ppIndex].statuses[sIndex]
  if (s.isNew) {
    positionPapers.value[ppIndex].statuses.splice(sIndex, 1)
  } else {
    Object.assign(s, s._backup, { editing: false })
  }
}

const savePPStatus = async (ppIndex, s, sIndex) => {
  const pp = positionPapers.value[ppIndex]
  try {
    if (s.isNew) {
      const res = await api.post(`/ao-tracker/${currentRecord.value.id}/position-papers/${pp.id}/statuses`, {
        status: s.status, date: s.date
      })
      positionPapers.value[ppIndex].statuses[sIndex] = { ...res.data, editing: false }
    } else {
      const res = await api.put(`/ao-tracker/${currentRecord.value.id}/position-papers/${pp.id}/statuses/${s.id}`, {
        status: s.status, date: s.date
      })
      Object.assign(positionPapers.value[ppIndex].statuses[sIndex], res.data, { editing: false })
    }
    delete s.isNew
    delete s._backup
  } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to save status', icon: 'error' }) }
}

const deletePPStatus = async (ppIndex, sIndex) => {
  const pp = positionPapers.value[ppIndex]
  const s = pp.statuses[sIndex]
  const result = await Swal.fire({ title: 'Delete?', text: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
  if (result.isConfirmed) {
    try {
      await api.delete(`/ao-tracker/${currentRecord.value.id}/position-papers/${pp.id}/statuses/${s.id}`)
      pp.statuses.splice(sIndex, 1)
    } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' }) }
  }
}


// ============================================================================
// REFERRAL INPUTS CRUD
// ============================================================================

const loadAgencyOptions = async () => {
  try {
    const res = await api.get('/agencies')
    agencyOptions.value = res.data
  } catch (e) { console.error(e) }
}

const addReferralInput = (id) => {
  referralInputs.value.push({ referred_to: '', received_date: null, pending_inputs: '', drive_link: null, referral_id: id })
}

const editReferralInput = (index) => {
  const ri = referralInputs.value[index]
  ri._backup = { id: ri.id, referred_to: ri.referred_to, received_date: ri.received_date, pending_inputs: ri.pending_inputs, drive_link: ri.drive_link, referral_id: ri.referral_id }
  ri.editing = true
}

const cancelReferralInput = (index) => {
  if (referralInputs.value[index].isNew) {
    referralInputs.value.splice(index, 1)
  } else {
    Object.assign(referralInputs.value[index], referralInputs.value[index]._backup, { editing: false })
  }
}

const saveReferralInput = async (data, index) => {
  riSaving.value = index
  try {
    if (data.isNew) {
      const res = await api.post(`/ao-tracker/${currentRecord.value.id}/referral-inputs`, {
        referred_to: data.referred_to, received_date: data.received_date, pending_inputs: data.pending_inputs
      })
      referralInputs.value[index] = { ...res.data, editing: false }
    } else {
      await api.put(`/ao-tracker/${currentRecord.value.id}/referral-inputs/${data.id}`, {
        referred_to: data.referred_to, received_date: data.received_date, pending_inputs: data.pending_inputs
      })
      data.editing = false
    }
    delete data.isNew
    delete data._backup
  } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' }) }
  finally { riSaving.value = null }
}

const deleteReferralInput = async (index) => {
  const ri = referralInputs.value[index]
  const result = await Swal.fire({ title: 'Delete?', text: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
  if (result.isConfirmed) {
    try {
      await api.delete(`/ao-tracker/${currentRecord.value.id}/referral-inputs/${ri.id}`)
      referralInputs.value.splice(index, 1)
    } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' }) }
  }
}

onMounted(() => {
  loadRecords()
  loadAgencyOptions()
  if (isDC.value) loadAOUsers()
  
  loadSubjectTitles()
  loadAuthors()
})

// ============================================================================
// KEY ISSUES CRUD
// ============================================================================

const addRevenueRow = () => {
  keyIssues.value.revenue_rows.push({ particulars_rows: [{ particulars: '', no_estimate: false }], current_existing_regime: '', proposal_salient_provisions: '', fpmg_recommendation_positions: '', osec_instruction_remarks: '' })
}

const addBudgetaryRow = () => {
  keyIssues.value.budgetary_rows.push({ particulars_rows: [{ particulars: '', no_estimate: false }], current_existing_regime: '', proposal_salient_provisions: '', fpmg_recommendation_positions: '', osec_instruction_remarks: '' })
}

const addDofConcernsRow = () => {
  keyIssues.value.dof_concerns_rows.push({ concerns: '', current_existing_regime: '', proposal_salient_provisions: '', fpmg_recommendation_positions: '', osec_instruction_remarks: '' })
}

const deleteRevenueRow = async (idx) => {
  const row = keyIssues.value.revenue_rows[idx]
  if (row.id) {
    const result = await Swal.fire({ title: 'Delete?', text: 'Delete this revenue impact row?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
    if (!result.isConfirmed) return
    try {
      await api.delete(`/ao-tracker/${currentRecord.value.id}/impacts/${row.id}`)
    } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' }); return }
  }
  keyIssues.value.revenue_rows.splice(idx, 1)
}

const deleteBudgetaryRow = async (idx) => {
  const row = keyIssues.value.budgetary_rows[idx]
  if (row.id) {
    const result = await Swal.fire({ title: 'Delete?', text: 'Delete this budgetary impact row?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
    if (!result.isConfirmed) return
    try {
      await api.delete(`/ao-tracker/${currentRecord.value.id}/impacts/${row.id}`)
    } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' }); return }
  }
  keyIssues.value.budgetary_rows.splice(idx, 1)
}

const deleteDofConcernsRow = async (idx) => {
  const row = keyIssues.value.dof_concerns_rows[idx]
  if (row.id) {
    const result = await Swal.fire({ title: 'Delete?', text: 'Delete this DOF concern?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
    if (!result.isConfirmed) return
    try {
      await api.delete(`/ao-tracker/${currentRecord.value.id}/dof-concerns/${row.id}`)
    } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' }); return }
  }
  keyIssues.value.dof_concerns_rows.splice(idx, 1)
}

const saveKeyIssues = async () => {
  keyIssuesSaving.value = true
  try {
    const refId = currentRecord.value.id

    // Save revenue rows
    for (let i = 0; i < keyIssues.value.revenue_rows.length; i++) {
      const row = keyIssues.value.revenue_rows[i]
      const amounts = {}
      Object.keys(row).forEach(k => { if (/^\d{4}$/.test(k)) amounts[k] = row[k] })
      if (row.id) {
        const res = await api.put(`/ao-tracker/${refId}/impacts/${row.id}`, { particulars: row.particulars, amounts })
        keyIssues.value.revenue_rows[i] = { ...res.data }
      } else {
        const res = await api.post(`/ao-tracker/${refId}/impacts`, { impact_type: 'revenue', particulars: row.particulars, amounts })
        keyIssues.value.revenue_rows[i] = { ...res.data }
      }
    }

    // Save budgetary rows
    for (let i = 0; i < keyIssues.value.budgetary_rows.length; i++) {
      const row = keyIssues.value.budgetary_rows[i]
      const amounts = {}
      Object.keys(row).forEach(k => { if (/^\d{4}$/.test(k)) amounts[k] = row[k] })
      if (row.id) {
        const res = await api.put(`/ao-tracker/${refId}/impacts/${row.id}`, { particulars: row.particulars, amounts })
        keyIssues.value.budgetary_rows[i] = { ...res.data }
      } else {
        const res = await api.post(`/ao-tracker/${refId}/impacts`, { impact_type: 'budgetary', particulars: row.particulars, amounts })
        keyIssues.value.budgetary_rows[i] = { ...res.data }
      }
    }

    // Save DOF concerns
    for (let i = 0; i < keyIssues.value.dof_concerns_rows.length; i++) {
      const row = keyIssues.value.dof_concerns_rows[i]
      if (row.id) {
        const res = await api.put(`/ao-tracker/${refId}/dof-concerns/${row.id}`, { concerns: row.concerns })
        keyIssues.value.dof_concerns_rows[i] = { ...res.data }
      } else {
        const res = await api.post(`/ao-tracker/${refId}/dof-concerns`, { concerns: row.concerns })
        keyIssues.value.dof_concerns_rows[i] = { ...res.data }
      }
    }

    Swal.fire({ title: 'Saved!', text: 'Key issues saved successfully', icon: 'success', timer: 1500, showConfirmButton: false })
  } catch (e) {
    Swal.fire({ title: 'Error!', text: 'Failed to save key issues', icon: 'error' })
  } finally {
    keyIssuesSaving.value = false
  }
}
</script>

<style scoped>
.page { padding: 1rem 0; background: var(--p-surface-50); min-height: 100vh; }
.container { backdrop-filter: blur(10px); border-radius: 20px; margin: 1rem; padding: 1.5rem; width: calc(100vw - 2rem); max-width: none; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 2px solid #1e3a8a; }
.page-header h1 { color: #1e3a8a; font-size: 2rem; font-weight: 700; }
.table-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.congress-filter {
  min-width: 200px;
}
.actions { display: flex; gap: 0.5rem; }

.detail-grid { display: flex; flex-direction: column; gap: 1.5rem; padding: 2rem; max-height: 75vh; overflow-y: auto; background: #f8fafc; }
.detail-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; padding: 1.25rem; background: white; border-radius: 10px; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.detail-item { display: flex; flex-direction: column; gap: 0.25rem; }
.detail-label { font-size: 0.75rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.03em; }
.detail-value { font-size: 0.9rem; color: #1e293b; font-weight: 500; word-wrap: break-word; overflow-wrap: break-word; word-break: break-all; max-width: 100%; }
.detail-value a { color: #1e3a8a; word-wrap: break-word; overflow-wrap: break-word; word-break: break-all; text-decoration: none; }
.detail-value a:hover { text-decoration: underline; }

.editable-section { display: flex; flex-direction: column; gap: 1rem; background: white; border-radius: 10px; padding: 1.5rem; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.editable-section .p-button { align-self: flex-start; }
.form-group { display: flex; flex-direction: column; }
.form-group .p-inputtext, .form-group .p-textarea, .form-group .p-select, .form-group .p-datepicker, .form-group .p-multiselect { width: 100% !important; }

.form-row-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
@media (max-width: 992px) { .form-row-3 { grid-template-columns: 1fr; } }

.assign-form { display: flex; flex-direction: column; gap: 1rem; padding: 1rem 0; }
.assign-form .p-select { width: 100% !important; }
.assign-form .info-card { display: flex; align-items: center; gap: 0.75rem; background: #dbeafe; color: #1e40af; padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.85rem; border-left: 4px solid #3b82f6; }

.field-with-add { display: flex; align-items: flex-start; gap: 0.5rem; }
.field-with-add .flex-grow { flex: 1; }
.field-with-add .p-select { width: 100% !important; }

.pp-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 1.25rem; background: white; display: flex; flex-direction: column; gap: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.pp-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 0.5rem; border-bottom: 1px solid #f1f5f9; }
.pp-header h4 { color: #1e3a8a; margin: 0; font-size: 0.95rem; font-weight: 700; }
.pp-fields { display: flex; flex-direction: column; gap: 0.75rem; }
.pp-fields .form-group .p-inputtext { width: 100% !important; }
.pp-statuses { margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid #f1f5f9; }
.pp-statuses h5 { color: #475569; margin: 0; font-size: 0.85rem; font-weight: 600; }

.key-issues-wrapper {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.5rem;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

:deep(.p-datatable .p-inputtext),
:deep(.p-datatable .p-textarea),

:deep(.p-datatable .p-datepicker),
:deep(.p-datatable .p-multiselect) {
  width: 100% !important;
}

:deep(.pp-card .p-inputtext),
:deep(.pp-card .p-textarea),
:deep(.pp-card .p-select),
:deep(.pp-card .p-datepicker),
:deep(.pp-card .p-multiselect) {
  width: 100% !important;
}

.modal-actions { display: flex; justify-content: flex-end; gap: 0.75rem; padding: 1.5rem; border-top: 1px solid #e5e7eb; background: #f8fafc; }
.modal-actions-right { display: flex; justify-content: flex-end; padding: 1.5rem; border-top: 1px solid #e5e7eb; background: #f8fafc; position: sticky; bottom: 0; z-index: 10; }

@media (max-width: 768px) { .detail-row { grid-template-columns: 1fr; } }

.view-controls { margin-bottom: 1rem; }
.view-buttons { display: flex; gap: 0.25rem; align-items: center; flex-wrap: wrap; }
.filter-select { min-width: 180px; }

.table-filters { display: flex; flex-direction: column; gap: 0.75rem; }
.filters-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.5rem; }
.search-field { grid-column: span 2; }
.search-field :deep(.p-inputtext) { height: 100%; min-height: 3rem; }
.filter-item { width: 100% !important; }
.filter-summary { display: flex; justify-content: space-between; align-items: center; padding-top: 0.5rem; border-top: 1px solid #f1f5f9; }
.result-count { font-size: 0.8rem; color: #64748b; font-weight: 500; }

.metrics-view { background: white; border-radius: 12px; padding: 1.5rem; border: 1px solid #e2e8f0; }
.metrics-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
.metric-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 1rem; }
.metric-card h4 { color: #1e3a8a; margin: 0 0 1rem 0; font-size: 0.95rem; }
.metric-bars { display: flex; flex-direction: column; gap: 0.5rem; }
.metric-bar-row { display: flex; align-items: center; gap: 0.5rem; }
.metric-label { font-size: 0.8rem; color: #374151; min-width: 80px; max-width: 150px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.metric-bar-container { flex: 1; height: 20px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
.metric-bar { height: 100%; background: linear-gradient(90deg, #1e3a8a, #3b82f6); border-radius: 4px; transition: width 0.3s; }
.metric-bar.pie-bar { background: linear-gradient(90deg, #10b981, #34d399); }
.metric-bar.stacked-bar { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.metric-bar.logged-bar { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.metric-count { font-size: 0.8rem; font-weight: 700; color: #1e3a8a; min-width: 30px; text-align: right; }
</style>
